import React, { useEffect, useState } from 'react';
import SimpleBar from 'simplebar-react';

import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

import BtnGradient from 'src/components/common/button/BtnGradient';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';
import InputCustom from 'src/components/common/field/InputCustom';
import DropDown from 'src/components/common/field/DropDown';
import { getToken } from 'src/utils/auth';
import { getAPI, postAPI } from 'src/utils/api';
import Skeleton from '@mui/material/Skeleton';
import { useNotify } from 'src/context/ContextNotify';
import { isEmpty, getField } from 'src/common/helper';
import { DetailDataType } from './DialogDetail';

const typographyLabel = {
  fontSize: '14px',
  lineHeight: '16.8px',
  fontWeight: 500,
  width: '110px'
};

const colorRequired = {
  color: '#ED5565',
  fontSize: '14px',
  fontWeight: '500'
};

const inputSX = {
  width: '340px',
  height: '48px'
};

const widthInput = {
  width: '340px'
};

const sxLableDropDown = {
  color: '#434154',
  '&.Mui-focused': { color: '#434154', transform: 'translate(14px, 16px) scale(1)' },
  '&.MuiInputLabel-root': { color: '#434154', transform: 'translate(14px, 16px) scale(1)' }
}

const StackCenter = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
});

const sizeSkeletoField = {
  width: '340px',
  height: '48px'
};

const defaultError = {
  product: true,
  vendorid: true,
  unit: true
};

const defaultForm = {
  id: null,
  vendor: '',
  vendorid: null,
  unit: '',
  displayorder: '',
  minstock: null,
  product: '',
  status: true
};

type DataFormType = {
  id: number | null;
  vendor: string;
  vendorid: number | null;
  unit: string;
  displayorder: string;
  minstock: number | null;
  product: string;
  status: boolean;
};

interface DialogFormProps {
  open: boolean;
  datas: DetailDataType | undefined;
  onClose: (isRefreshList?: boolean) => void;
};

const DialogForm: React.FC<DialogFormProps> = (props) => {
  const notifyContext = useNotify();
  const [dataForm, setDataForm] = useState<DataFormType>(defaultForm);
  const [vendors, setVendors] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState(defaultError);

  const convertItemType = (data: any[], id: string, name: string) => {
    const converted = data
      ? data.map((item: any) => ({
        id: String(item[id]),
        name: item[name],
      }))
      : [];

    return converted;
  };

  const handleChange = (name: string) => (value: boolean | number | string | number[] | null) => {
    setDataForm(prevState => ({
      ...prevState,
      [name]: value
    }))
  };

  const handleClose = () => {
    props.onClose();
  };

  const validate = () => {
    if (isEmpty(dataForm.product)) {
      setErrors({ ...defaultError, product: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '製品を入力してください',
        type: 'error'
      })
      return false;
    }
    if (isEmpty(dataForm.vendorid)) {
      setErrors({ ...defaultError, vendorid: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: 'ベンダーを選択してください',
        type: 'error'
      })
      return false;
    }
    if (isEmpty(dataForm.unit)) {
      setErrors({ ...defaultError, unit: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '単位を入力してください',
        type: 'error'
      })
      return false;
    }

    setErrors(defaultError);

    notifyContext.setNotifyMessage({
      show: false,
      message: '',
      type: 'error'
    });

    return true;
  };


  const handleSave = () => {
    if (!validate()) {
      return;
    }
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      const data = dataForm;

      postAPI(`/master/product-op-save?&token=${token}`, data)
        .then((res: any) => {
          if (res.code === 200) {
            notifyContext.setNotifyMessage({
              show: true,
              message: '保存出来ました。',
              type: 'success',
            });
            props.onClose(true);
          }
          if (res.code === 500 || res.code === 501) {
            notifyContext.setNotifyMessage({
              show: true,
              message: res.msg || 'Failed to post data',
              type: 'error'
            });
          }
        })
        .finally(() => {
        });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const getDetail = () => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      setIsLoading(true);
      getAPI(`/master/product-op-view?token=${token}`)
        .then((res: any) => {
          if (res.code === 200 && res.data) {
            setVendors(convertItemType(getField(res.data, 'vendors', []), 'value', 'label'));
            setDataDefault(getField(res.data, 'product', {}))
            setIsLoading(false);
          }
        })
        .finally(() => {
        });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };


  const handleChangeSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    handleChange(name)(checked);
  };

  const setDataDefault = (product: any) => {
    setDataForm({
      id: getField(product, 'id', null),
      vendor: getField(product, 'vendor', null),
      vendorid: getField(product, 'vendorid', null),
      unit: getField(product, 'unit', null),
      displayorder: getField(product, 'displayorder', null),
      minstock: getField(product, 'minstock', null),
      product: getField(product, 'product', null),
      status: getField(product, 'status', false),
    })
  };

  const getInit = () => {
    if (props.datas) {
      setVendors(props.datas.vendors ? convertItemType(props.datas.vendors, 'value', 'label') : []);
      setDataDefault(props.datas.product ? props.datas.product : {});
    } else {
      getDetail();
    }
  };

  useEffect(() => {
    if (props.open) {
      getInit();
    } else {
      setDataForm(defaultForm);
    }
    // eslint-disable-next-line
  }, [props.open]);

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: '500px',
          m: '0px'
        }
      }}
    >
      <DialogTitle id='scroll-dialog-title' sx={{ p: '24px', pb: '0px' }}>
        <Stack direction={'row'} justifyContent={'space-between'}>
          <Typography sx={{ fontSize: '20px', lineHeight: '24px', fontWeight: 500 }}>
            {props.datas ? '手術材料更新' : '新規手術材料登録'}
          </Typography>
          <IconButton
            aria-label='Close'
            edge='end'
            onClick={handleClose}
          >
            <Box
              component={'img'}
              src='/images/ic-close-blue.svg'
              alt='icon eye'
              sx={{ width: '24px', height: '24px' }}
            />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          p: '0px 10px 24px 24px',
          '& .simplebar-track.simplebar-vertical .simplebar-scrollbar:before': {
            backgroundColor: '#CCD1D9',
          },
          overflow: 'hidden'
        }}
      >
        <SimpleBar
          style={{ height: 'calc(100vh - 210px)', borderWidth: '18px' }}
        >
          <Stack spacing='10px' sx={{ pr: '14px', mt: '10px' }}>
            <StackCenter>
              <Typography sx={{ ...typographyLabel }}>
                材料名
                <Typography component='label' {...colorRequired}>
                  *
                </Typography>
              </Typography>

              {!isLoading ? (
                <InputCustom
                  value={dataForm.product}
                  type='text'
                  name='product'
                  labelInput='物販名'
                  onChange={handleChange('product')}
                  sx={{
                    ...widthInput
                  }}
                  error={!errors.product}
                />
              ) : (
                <Skeleton variant='rounded' sx={{ ...sizeSkeletoField }} />
              )}
            </StackCenter>

            <Box sx={{ m: '15px 0px 5px !important' }}><Divider /></Box>

            <StackCenter>
              <Typography sx={{ ...typographyLabel }}>
                ベンダー
                <Typography component='label' {...colorRequired}>
                  *
                </Typography>
              </Typography>
              {!isLoading ? (
                <DropDown
                  selected={dataForm.vendorid}
                  idSelect='vendor-select'
                  placeholder='選択してください'
                  items={vendors}
                  onChange={handleChange('vendorid')}
                  sxInput={{ ...inputSX }}
                  sxLabel={sxLableDropDown}
                  error={!errors.vendorid}
                />
              ) : (
                <Skeleton variant='rounded' sx={{ ...sizeSkeletoField }} />
              )}
            </StackCenter>

            <Box sx={{ m: '15px 0px 5px !important' }}><Divider /></Box>

            <StackCenter>
              <Typography sx={{ ...typographyLabel }}>
                最低在庫量
              </Typography>

              {!isLoading ? (
                <InputCustom
                  value={dataForm.minstock}
                  type='number'
                  name='minstock'
                  labelInput='0'
                  onChange={handleChange('minstock')}
                  sx={{
                    ...widthInput
                  }}
                />
              ) : (
                <Skeleton variant='rounded' sx={{ ...sizeSkeletoField }} />
              )}
            </StackCenter>

            <Box sx={{ m: '15px 0px 5px !important' }}><Divider /></Box>

            <StackCenter>
              <Typography sx={{ ...typographyLabel }}>
                単位
                <Typography component='label' {...colorRequired}>
                  *
                </Typography>
              </Typography>

              {!isLoading ? (
                <InputCustom
                  value={dataForm.unit}
                  type='text'
                  name='unit'
                  labelInput='V'
                  onChange={handleChange('unit')}
                  sx={{
                    ...widthInput
                  }}
                  error={!errors.unit}
                />
              ) : (
                <Skeleton variant='rounded' sx={{ ...sizeSkeletoField }} />
              )}
            </StackCenter>

            <Box sx={{ m: '15px 0px 5px !important' }}><Divider /></Box>

            <StackCenter>
              <Typography sx={{ ...typographyLabel }}>
                ステータス
              </Typography>

              {!isLoading ? (
                <FormControlLabel
                  control={
                    <Switch
                      size='medium'
                      sx={{ mr: '7px' }}
                      checked={Boolean(dataForm.status)}
                      name='status'
                      onChange={(event) => handleChangeSwitch(event)}
                    />
                  }
                  label='有効'
                  sx={{ ml: '0px', '& .MuiTypography-root': { fontSize: '14px' } }}
                />
              ) : (
                <Skeleton variant='rounded' sx={{ ...sizeSkeletoField }} />
              )}
            </StackCenter>

          </Stack>
        </SimpleBar>
      </DialogContent>

      <DialogActions
        style={{
          justifyContent: 'right',
          padding: '16px'
        }}
      >
        <BtnOutlineGradient text='キャンセル' handleButtonClick={handleClose} disabled={isLoading} />
        <BtnGradient text='登録' handleButtonClick={handleSave} disabled={isLoading} />
      </DialogActions>
    </Dialog>
  );
};

export default DialogForm;
